import { useInput, useInputButton, wrappedInputPropsAdapter } from "../../input";
import { Box } from "../../box";
import { forwardRef, useCallback, useLayoutEffect, useState } from "react";
import { cssModule, isNil, mergeProps, useChainedEventCallback, useControllableState } from "../../shared";
import { useResponsiveValue } from "../../styling";
import { useFieldInputProps } from "../../field";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultElement = "textarea";

const pxToInt = value => {
  return !isNil(value) ? parseInt(value.replace("px", ""), 10) : 0;
};

export function InnerTextArea(props) {
  const [fieldProps] = useFieldInputProps();
  const {
    active,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    as = DefaultElement,
    autoFocus,
    button,
    defaultValue,
    disabled,
    fluid,
    focus,
    forwardedRef,
    hover,
    id,
    loading,
    maxRows,
    onChange,
    onValueChange,
    placeholder,
    readOnly,
    required,
    rows: rowsProp,
    type = "text",
    validationState,
    value,
    wrapperProps: {
      as: wrapperAs = "div",
      ...userWrapperProps
    } = {},
    ...rest
  } = mergeProps(props, wrappedInputPropsAdapter(fieldProps));

  if (isNil(ariaLabel) && isNil(ariaLabelledBy) && isNil(placeholder)) {
    console.error("An input component must either have an \"aria-label\" attribute, an \"aria-labelledby\" attribute or a \"placeholder\" attribute.");
  }

  const fluidValue = useResponsiveValue(fluid);
  const [inputValue, setValue] = useControllableState(value, defaultValue, "");
  const [rows, setRows] = useState(rowsProp);
  const handleChange = useChainedEventCallback(onChange, event => {
    const newValue = event.target.value;
    setValue(newValue);

    if (!isNil(onValueChange)) {
      onValueChange(event, newValue);
    }
  });
  const {
    inputProps,
    inputRef,
    wrapperProps
  } = useInput({
    active,
    autoFocus,
    cssModule: "o-ui-text-area",
    disabled,
    fluid: fluidValue,
    focus,
    forwardedRef,
    hover,
    id,
    loading,
    onChange: handleChange,
    placeholder,
    readOnly,
    required,
    type,
    validationState,
    value: inputValue
  });
  const adjustRows = useCallback(() => {
    const input = inputRef.current;
    const {
      fontSize,
      lineHeight,
      paddingBottom,
      paddingTop
    } = window.getComputedStyle(input);
    const padding = pxToInt(paddingTop) + pxToInt(paddingBottom);
    const currentRows = Math.floor((input.scrollHeight - padding) / pxToInt(lineHeight !== "normal" ? lineHeight : fontSize));
    const newRows = !isNil(maxRows) && currentRows > maxRows ? maxRows : currentRows;
    setRows(newRows);
  }, [inputRef, maxRows]);
  useLayoutEffect(() => {
    adjustRows();
  }, [adjustRows, inputValue]);
  const buttonMarkup = useInputButton(button, !disabled && !readOnly);

  const content = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Box, { ...mergeProps(rest, {
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledBy,
        as,
        rows
      }, inputProps)
    }), buttonMarkup]
  });

  return /*#__PURE__*/_jsx(Box, { ...mergeProps(userWrapperProps, {
      as: wrapperAs,
      className: cssModule("o-ui-input", buttonMarkup && "has-button")
    }, wrapperProps),
    children: content
  });
}
InnerTextArea.defaultElement = DefaultElement;
export const TextArea = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerTextArea, { ...props,
  forwardedRef: ref
}));